import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CalendarDays, EyeOff, Plus } from 'lucide-react';
import { HelpTrigger } from '../../../help';
import { DeleteIcon, EditIcon, SpinnerIcon } from '../../../components/icons';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ActionsHeaderHelp from '../../../components/ActionsHeaderHelp';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import { useMilestoneTypes, type MilestoneType } from '../../hooks/useMilestoneTypes';
import { describeLeadDays, upcomingReminderDates } from '../../../constants/elections';
import { formatDate } from '../../lib/format';
import { MILESTONE_DOMAINS, type MilestoneDomain } from '../../lib/milestoneDomains';

/**
 * A milestone as a cycle detail serves it (election_milestones_resolved, or
 * legislative_cycle_milestones_resolved — same columns, plus session_key).
 */
export interface CycleMilestone {
  id: string;
  session_key?: string | null;
  milestone_type: string;
  milestone_date: string;
  label: string | null;
  display_label: string;
  type_label: string;
  sort_order: number | null;
  notes: string | null;
  category: string;
  citizen_visible: boolean;
  notify_citizens: boolean;
  will_notify: boolean;
  citizen_reminder_lead_days: number[];
  type_active: boolean;
}

/** A session a legislative milestone can belong to. */
export interface MilestoneSessionOption {
  session_key: string;
  short_label: string;
}

interface CycleMilestonesCardProps {
  /** The election calendar or legislative cycle the milestones belong to. */
  cycleId: string;
  milestones: CycleMilestone[];
  /** Which kind of cycle (default election) — see admin/lib/milestoneDomains. */
  domain?: MilestoneDomain;
  /** Legislative only: the cycle's sessions, offered as the milestone's session. */
  sessions?: MilestoneSessionOption[];
  /** The cycle-detail query key — patched optimistically by the inline controls. */
  cycleQueryKey: readonly unknown[];
  /** Refetch everything that shows this cycle (detail, list, audit). */
  onChanged: () => void;
}

const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';

const EMPTY_FORM = {
  id: '',
  session_key: '',
  milestone_type: '',
  milestone_date: '',
  label: '',
  sort_order: '',
  notes: '',
  notify_citizens: false,
};

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

const localTodayISO = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const dayOf = (date: string) => date.slice(0, 10);

const listDates = (dates: string[]) => {
  const formatted = dates.map((date) => formatDate(date));
  if (formatted.length <= 1) return formatted[0] ?? '';
  return `${formatted.slice(0, -1).join(', ')} and ${formatted[formatted.length - 1]}`;
};

/**
 * Inline sort-order editor: saves on blur or Enter when the value changed,
 * Escape reverts, anything that isn't a whole number reverts without a request.
 */
const OrderCell: React.FC<{
  milestone: CycleMilestone;
  saving: boolean;
  onSave: (value: number) => void;
}> = ({ milestone, saving, onSave }) => {
  const stored = String(milestone.sort_order ?? 0);
  const [draft, setDraft] = useState(stored);
  useEffect(() => setDraft(stored), [stored]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === stored) return;
    if (!/^-?\d+$/.test(trimmed)) {
      setDraft(stored);
      return;
    }
    onSave(parseInt(trimmed, 10));
  };

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        value={draft}
        disabled={saving}
        aria-label={`Sort order for ${milestone.display_label}`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          } else if (event.key === 'Escape') {
            setDraft(stored);
          }
        }}
        className="input w-[4.5rem] px-2 py-1"
      />
      {saving && <SpinnerIcon className="h-4 w-4 animate-spin text-text-tertiary" aria-hidden />}
    </div>
  );
};

/**
 * The cycle's Milestones card (Documentation/ELECTION_MILESTONE_TYPES_PRD.md
 * §5.5–5.5a): every date with its order, and whether it sends citizen
 * reminders — both editable in place. Unchecking Notify saves at once (the
 * safe direction); checking it confirms with the concrete send dates first.
 *
 * Shared by election and legislative cycles (ELECTION_OUTCOMES_PRD.md §5.0). A
 * domain without citizen reminders hides the Notify column and controls.
 */
const CycleMilestonesCard: React.FC<CycleMilestonesCardProps> = ({
  cycleId,
  milestones,
  domain = 'election',
  sessions,
  cycleQueryKey,
  onChanged,
}) => {
  const queryClient = useQueryClient();
  const todayISO = localTodayISO();
  const config = MILESTONE_DOMAINS[domain];
  const reminders = config.citizenReminders;
  const columnCount = reminders ? 5 : 4;
  const types = useMilestoneTypes({ domain });
  const [modal, setModal] = useState<'add' | 'edit' | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // On add, Notify and Sort Order follow the Type select until the admin touches them.
  const [touched, setTouched] = useState({ notify: false, sort: false });
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const [notifyConfirm, setNotifyConfirm] = useState<CycleMilestone | null>(null);
  const [savingOrderId, setSavingOrderId] = useState<string | null>(null);

  const typeOptions = useMemo(() => {
    const active = [...(types.data ?? [])].sort((a, b) => a.label.localeCompare(b.label));
    const current = milestones.find((m) => m.id === form.id);
    if (modal === 'edit' && current && !current.type_active && !active.some((t) => t.value === current.milestone_type)) {
      active.push({
        value: current.milestone_type,
        label: `${current.type_label} (retired)`,
        citizen_visible: current.citizen_visible,
        citizen_reminder_lead_days: current.citizen_reminder_lead_days,
      } as MilestoneType);
    }
    return active;
  }, [types.data, milestones, form.id, modal]);

  const selectedType = typeOptions.find((type) => type.value === form.milestone_type);

  /** One-field PUT with an optimistic patch of the cycle query, rolled back on error. */
  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CycleMilestone> }) =>
      api.put(`${config.milestonesPath(cycleId)}/${id}`, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: cycleQueryKey });
      const previous = queryClient.getQueryData(cycleQueryKey);
      queryClient.setQueryData(cycleQueryKey, (old: any) =>
        old
          ? {
            ...old,
            milestones: old.milestones.map((m: CycleMilestone) =>
              m.id === id
                ? { ...m, ...patch, will_notify: (patch.notify_citizens ?? m.notify_citizens) && m.citizen_visible }
                : m
            ),
          }
          : old
      );
      return { previous };
    },
    onError: (error: any, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(cycleQueryKey, context.previous);
      toast.error(apiError(error, 'Failed to update milestone'));
    },
    onSettled: () => {
      setSavingOrderId(null);
      onChanged();
    },
  });

  const setNotify = (milestone: CycleMilestone, notify: boolean) => {
    patchMutation.mutate(
      { id: milestone.id, patch: { notify_citizens: notify } },
      {
        onSuccess: () =>
          toast.success(
            notify
              ? `Citizens will be reminded about ${milestone.display_label}.`
              : `Citizens won't be reminded about ${milestone.display_label}.`
          ),
      }
    );
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        milestone_type: form.milestone_type,
        milestone_date: form.milestone_date,
        label: form.label.trim(),
        notes: form.notes.trim(),
        ...(reminders
          ? { notify_citizens: selectedType?.citizen_visible === false ? false : form.notify_citizens }
          : {}),
        ...(sessions ? { session_key: form.session_key } : {}),
        ...(form.sort_order.trim() !== '' ? { sort_order: parseInt(form.sort_order, 10) } : {}),
      };
      return modal === 'add'
        ? api.post(config.milestonesPath(cycleId), payload)
        : api.put(`${config.milestonesPath(cycleId)}/${form.id}`, payload);
    },
    onSuccess: () => {
      toast.success(modal === 'add' ? 'Milestone added' : 'Milestone updated');
      setModal(null);
      setForm(EMPTY_FORM);
      onChanged();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to save milestone')),
  });

  const deleteMutation = useMutation({
    mutationFn: (milestoneId: string) => api.delete(`${config.milestonesPath(cycleId)}/${milestoneId}`),
    onSuccess: () => {
      toast.success('Milestone deleted');
      setDeleteTarget(null);
      onChanged();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to delete milestone')),
  });

  const chooseType = (value: string) => {
    const type = (types.data ?? []).find((t) => t.value === value);
    setForm((current) => {
      const next = { ...current, milestone_type: value };
      if (!type) return next;
      if (!type.citizen_visible) next.notify_citizens = false;
      else if (modal === 'add' && !touched.notify) next.notify_citizens = type.notify_by_default;
      if (modal === 'add' && !touched.sort) next.sort_order = String(type.default_sort_order);
      return next;
    });
  };

  const openAdd = () => {
    const first = [...(types.data ?? [])].sort((a, b) => a.label.localeCompare(b.label))[0];
    setTouched({ notify: false, sort: false });
    setForm({
      ...EMPTY_FORM,
      milestone_type: first?.value ?? '',
      notify_citizens: first?.notify_by_default ?? false,
      sort_order: first ? String(first.default_sort_order) : '',
    });
    setModal('add');
  };

  const openEdit = (milestone: CycleMilestone) => {
    setTouched({ notify: true, sort: true });
    setForm({
      id: milestone.id,
      milestone_type: milestone.milestone_type,
      milestone_date: dayOf(milestone.milestone_date),
      session_key: milestone.session_key ?? '',
      label: milestone.label ?? '',
      sort_order: String(milestone.sort_order ?? 0),
      notes: milestone.notes ?? '',
      notify_citizens: milestone.notify_citizens,
    });
    setModal('edit');
  };

  const confirmDates = notifyConfirm
    ? upcomingReminderDates(notifyConfirm.milestone_date, notifyConfirm.citizen_reminder_lead_days, todayISO)
    : [];

  const editingCurrent = milestones.find((m) => m.id === form.id);
  const forcedOff =
    modal === 'edit' && selectedType?.citizen_visible === false && editingCurrent?.notify_citizens === true;

  return (
    <div className="card p-5">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-text-primary dark:text-text-dark-primary">Milestones</h4>
          {reminders
            ? <HelpTrigger contentKey="admin-milestone-types" size="sm" ariaLabel="What the Notify box and Order do" />
            : <HelpTrigger contentKey="legislative-cycles" size="sm" ariaLabel="What legislative milestones are for" />}
        </span>
        <Button variant="ghost" size="sm" icon={Plus} disabled={types.isLoading} onClick={openAdd}>
          Add Milestone
        </Button>
      </div>
      <p className="mb-3 text-xs text-text-tertiary dark:text-text-dark-tertiary">
        {reminders
          ? 'Checked dates send citizen reminders. Order sets the display order wherever these dates are listed.'
          : 'Order sets the display order wherever these dates are listed.'}
      </p>

      <StandardTable horizontalScroll variant="compact">
        <StandardTableHeader>
          <StandardTableRow>
            <StandardTableHeaderCell columnType="narrow">Order</StandardTableHeaderCell>
            <StandardTableHeaderCell columnType="nowrap">Date</StandardTableHeaderCell>
            <StandardTableHeaderCell>Milestone</StandardTableHeaderCell>
            {reminders && <StandardTableHeaderCell columnType="narrow" align="center">Notify</StandardTableHeaderCell>}
            <StandardTableHeaderCell columnType="actions">
              <span className="inline-flex items-center gap-1.5">
                Actions
                <ActionsHeaderHelp icons={['edit', 'delete']} />
              </span>
            </StandardTableHeaderCell>
          </StandardTableRow>
        </StandardTableHeader>
        <StandardTableBody>
          {milestones.length === 0 ? (
            <EmptyTableState
              colSpan={columnCount}
              message={config.emptyMilestonesMessage}
              icon={<CalendarDays className="h-8 w-8" />}
            />
          ) : (
            milestones.map((milestone) => {
              const past = dayOf(milestone.milestone_date) < todayISO;
              const schedule = describeLeadDays(milestone.citizen_reminder_lead_days);
              const notifyTitle = !milestone.citizen_visible
                ? `${milestone.type_label} milestones are hidden from citizens and can't notify`
                : past
                  ? 'This date has passed — nothing more will send'
                  : `Reminders ${schedule}`;
              return (
                <StandardTableRow key={milestone.id}>
                  <StandardTableCell>
                    <OrderCell
                      milestone={milestone}
                      saving={savingOrderId === milestone.id}
                      onSave={(value) => {
                        setSavingOrderId(milestone.id);
                        patchMutation.mutate({ id: milestone.id, patch: { sort_order: value } });
                      }}
                    />
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="whitespace-nowrap font-medium text-text-primary dark:text-text-dark-primary">
                      {formatDate(milestone.milestone_date)}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="text-text-primary dark:text-text-dark-primary">{milestone.display_label}</span>
                      {milestone.label && (
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">({milestone.type_label})</span>
                      )}
                      {sessions && milestone.session_key && (
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          {sessions.find((session) => session.session_key === milestone.session_key)?.short_label ?? milestone.session_key}
                        </span>
                      )}
                      {!milestone.type_active && (
                        <span className="rounded-full bg-background-secondary px-2 py-0.5 text-xs text-text-secondary dark:bg-background-dark-tertiary dark:text-text-dark-secondary">
                          Retired type
                        </span>
                      )}
                      {reminders && !milestone.citizen_visible && (
                        <span className="inline-flex items-center gap-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          <EyeOff className="h-3 w-3" aria-hidden /> Hidden from citizens
                        </span>
                      )}
                    </div>
                  </StandardTableCell>
                  {reminders && (
                  <StandardTableCell align="center">
                    <input
                      type="checkbox"
                      checked={milestone.will_notify}
                      disabled={!milestone.citizen_visible || past}
                      title={notifyTitle}
                      aria-label={`Notify citizens about ${milestone.display_label}`}
                      onChange={(event) => {
                        if (event.target.checked) setNotifyConfirm(milestone);
                        else setNotify(milestone, false);
                      }}
                      className="h-4 w-4 cursor-pointer rounded border-border-light-strong text-primary-600 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-border-dark"
                    />
                  </StandardTableCell>
                  )}
                  <StandardTableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" icon={EditIcon} ariaLabel={`Edit ${milestone.display_label}`}
                        title={`Edit ${milestone.display_label}`} onClick={() => openEdit(milestone)} />
                      <Button variant="ghost" size="sm" icon={DeleteIcon} ariaLabel={`Delete ${milestone.display_label}`}
                        title={`Delete ${milestone.display_label}`}
                        onClick={() => setDeleteTarget({ id: milestone.id, label: milestone.display_label })} />
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              );
            })
          )}
        </StandardTableBody>
      </StandardTable>

      <ConfirmationDialog
        isOpen={notifyConfirm !== null}
        title="Remind citizens?"
        message={
          notifyConfirm
            ? confirmDates.length > 0
              ? `Remind citizens about ${notifyConfirm.display_label}? Every citizen with election reminders on gets a notification on ${listDates(confirmDates)}.`
              : `All reminder dates for ${notifyConfirm.display_label} have passed — nothing will send. Check it anyway?`
            : ''
        }
        confirmText="Remind citizens"
        variant="warning"
        onConfirm={() => {
          if (notifyConfirm) setNotify(notifyConfirm, true);
          setNotifyConfirm(null);
        }}
        onCancel={() => setNotifyConfirm(null)}
      />

      <AdminModal
        isOpen={modal !== null}
        title={modal === 'add' ? 'Add Milestone' : 'Edit Milestone'}
        onClose={() => setModal(null)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="primary" size="sm" disabled={form.milestone_date === '' || form.milestone_type === ''}
              loading={saveMutation.isLoading} onClick={() => saveMutation.mutate()}>
              Save Milestone
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="milestone-type" className={labelClasses}>Type</label>
              <select id="milestone-type" value={form.milestone_type}
                onChange={(event) => chooseType(event.target.value)} className="input">
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              {reminders && selectedType && (
                <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {selectedType.citizen_visible ? 'Citizens see this date.' : "Hidden from citizens — can't notify."}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="milestone-date" className={labelClasses}>Date *</label>
              <input id="milestone-date" type="date" value={form.milestone_date}
                onChange={(event) => setForm((current) => ({ ...current, milestone_date: event.target.value }))}
                className="input" />
            </div>
          </div>
          {sessions && (
            <div>
              <label htmlFor="milestone-session" className={labelClasses}>Session</label>
              <select id="milestone-session" value={form.session_key}
                onChange={(event) => setForm((current) => ({ ...current, session_key: event.target.value }))}
                className="input">
                <option value="">Whole cycle</option>
                {sessions.map((session) => (
                  <option key={session.session_key} value={session.session_key}>{session.short_label}</option>
                ))}
              </select>
            </div>
          )}
          {reminders && (
          <div>
            <div className="flex items-center gap-2">
              <input id="milestone-notify" type="checkbox"
                checked={selectedType?.citizen_visible === false ? false : form.notify_citizens}
                disabled={selectedType?.citizen_visible === false}
                onChange={(event) => {
                  setTouched((current) => ({ ...current, notify: true }));
                  setForm((current) => ({ ...current, notify_citizens: event.target.checked }));
                }}
                className="h-4 w-4 rounded border-border-light-strong text-primary-600 focus:ring-primary-500 disabled:opacity-50 dark:border-border-dark" />
              <label htmlFor="milestone-notify" className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                Notify citizens
              </label>
            </div>
            <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">
              {selectedType?.citizen_visible === false
                ? forcedOff
                  ? "This type is hidden from citizens, so this date will stop notifying when you save."
                  : "This type is hidden from citizens, so its dates can't notify."
                : `Citizens with election reminders on are reminded ${describeLeadDays(selectedType?.citizen_reminder_lead_days)}.`}
            </p>
          </div>
          )}
          <div>
            <label htmlFor="milestone-label" className={labelClasses}>Label</label>
            <input id="milestone-label" type="text" value={form.label}
              placeholder={selectedType ? `Defaults to “${selectedType.label.replace(/ \(retired\)$/, '')}”` : 'Defaults to the type name'}
              onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
              className="input" />
          </div>
          <div>
            <label htmlFor="milestone-sort" className={labelClasses}>Sort Order</label>
            <input id="milestone-sort" type="number" value={form.sort_order}
              onChange={(event) => {
                setTouched((current) => ({ ...current, sort: true }));
                setForm((current) => ({ ...current, sort_order: event.target.value }));
              }}
              className="input" />
          </div>
          <div>
            <label htmlFor="milestone-notes" className={labelClasses}>Notes</label>
            <textarea id="milestone-notes" rows={2} value={form.notes}
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              className="input" />
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={deleteTarget !== null}
        title="Delete milestone"
        message={
          deleteTarget ? config.deleteMilestoneMessage(deleteTarget.label) : ''
        }
        confirmText="Delete"
        variant="danger"
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default CycleMilestonesCard;
